import { TSQFunction } from "../../TSQ/TSQFunction"
import { OpaqueString } from "../Opaque"
import { TypeValidator } from "./TypeValidator"
import { YearMonthDate } from "./YearMonthDate"

export type DateTimeUnit = "year" | "month" | "day" | "hour" | "minute" | "second" | "millisecond"

/**
 * @shared
 */
export type DateTimePrecision =
    | DateTimeUnit
    | {
          unit: DateTimeUnit
          /** How many of the units represents one step. Defaults to 1 if not specified */
          units?: number
      }

/**
 * Parses a string as documented in `@precision.md`
 */
export function ParseDateTimePrecision(s: string): DateTimePrecision | undefined {
    const parts = s.split(" ")
    const units = parts.length === 1 ? 1 : parseInt(parts[0])
    let unit = parts[parts.length - 1].toLowerCase()

    // allow both plural and singular
    if (unit.endsWith("s")) unit = unit.substring(0, unit.length - 1)

    switch (unit) {
        case "year":
        case "month":
        case "day":
        case "hour":
        case "minute":
        case "second":
        case "millisecond":
            return { unit, units }
    }

    throw new Error("Invalid date time precision: " + JSON.stringify(s))
}

/**
 * Represents a timestamp in ISO 8601 / RFC 3339 format.
 *
 * Example of allowed values:
 * - In Zulu form: `2021-12-14T17:31:21Z`.
 * - With offsets: `2021-12-14T17:31:21+01:00` or `2021-12-14T17:31:21-01:00`.
 *
 * @shared
 */
export type Timestamp = OpaqueString<"Timestamp">
TypeValidator(Timestamp)
TSQFunction(Timestamp)
/**
 * Represents a timestamp in ISO 8601 format, e.g. `2021-12-14T17:31:21Z`.
 *
 * When stored in the database (as a column in table), this is stored as a `TIMESTAMP` type.
 *
 * When stored as event data, this is stored as a string.
 *
 * When stored in the database (as a JSONB column), this is stored as a string.
 */
export function Timestamp(
    d: string | Date | number | Timestamp | YearMonthDate = Date.now()
): Timestamp {
    return new Date(d.valueOf()).toISOString() as any as Timestamp
}

/**
 * Represents a naive timestamp , e.g. `2021-12-14 17:31:21`.
 *
 * This is a timestamp without timezone information. To know what it represents,
 * you must know the timezone from context.
 */
export type TimestampWithoutTimezone = OpaqueString<"TimestampWithoutTimezone">
TypeValidator(TimestampWithoutTimezone)
TSQFunction(TimestampWithoutTimezone)
/**
 * Represents a naive timestamp , e.g. `2021-12-14 17:31:21`.
 *
 * This is a timestamp without timezone information. To know what it represents,
 * you must know the timezone from context.
 *
 * When such timestamps are returned from Postgres, and the milliseconds are all
 * zeroes, they are omitted. This function behaves the same way, to ensure
 * consistent results across JS/SQL.
 */
export function TimestampWithoutTimezone(d: moment.Moment): TimestampWithoutTimezone {
    return (d.millisecond()
        ? d.format("YYYY-MM-DD HH:mm:ss.SSS")
        : d.format("YYYY-MM-DD HH:mm:ss")) as any as TimestampWithoutTimezone
}

/** Formats the string in YYYY[-MM[-DD[Thh:mm[:ss[.000]]]]] format in local time, according to the
 * specified precision. This is the format expected by HTML's <input> tag, conveniently. */
Timestamp.toLocal = function (ds: Timestamp, precision: DateTimePrecision) {
    function pad(s: string | number) {
        s = s.toString()
        if (s.length === 1) return "0" + s
        return s
    }

    const unit = typeof precision === "string" ? precision : precision.unit

    const d = new Date(ds.valueOf())
    if (unit === "year") {
        return `${d.getFullYear().toString()}` as any
    } else if (unit === "month") {
        return `${d.getFullYear().toString()}-${pad(d.getMonth() + 1)}` as any
    } else {
        const date = `${d.getFullYear().toString()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

        if (unit === "day") {
            return date as any
        } else {
            const hourAndMinutes = pad(d.getHours()) + ":" + pad(d.getMinutes())

            if (unit === "minute") {
                return (date + "T" + hourAndMinutes) as any
            } else {
                const hoursAndMinutesAndSeconds = `${hourAndMinutes}:${pad(d.getSeconds())}`

                if (unit === "second") {
                    return (date + "T" + hoursAndMinutesAndSeconds) as any
                } else if (unit === "millisecond") {
                    let ms = d.getMilliseconds().toString()
                    while (ms.length < 3) ms = "0" + ms
                    return `${date}T${hoursAndMinutesAndSeconds}.${ms}` as any
                }
            }
        }
    }

    throw new Error("Invalid timestamp precision: " + JSON.stringify(precision))
}

/**
 * Checks if date `a` is after date `b`.
 */
Timestamp.isAfter = function (
    a: Timestamp | number | string | Date | YearMonthDate,
    b: Timestamp | number | string | Date | YearMonthDate
) {
    return new Date(a.valueOf()) > new Date(b.valueOf())
}

/**
 * Checks if date `a` is before date `b`.
 */
Timestamp.isBefore = function (
    a: Timestamp | number | string | Date | YearMonthDate,
    b: Timestamp | number | string | Date | YearMonthDate
) {
    return new Date(a.valueOf()) < new Date(b.valueOf())
}

/**
 * Checks if date `a` is identical to date `b` (milliseconds are the same)
 */
Timestamp.isSame = function (
    a: Timestamp | number | string | Date | YearMonthDate,
    b: Timestamp | number | string | Date | YearMonthDate
) {
    return new Date(a.valueOf()).getTime() === new Date(b.valueOf()).getTime()
}

/**
 * Sort function for Timestamps, sorts ascending
 */
Timestamp.sortAscending = function (a: Timestamp | YearMonthDate, b: Timestamp | YearMonthDate) {
    return new Date(a.valueOf()).getTime() - new Date(b.valueOf()).getTime()
}

/**
 * Sort function for Timestamps, sorts descending
 */
Timestamp.sortDescending = function (a: Timestamp | YearMonthDate, b: Timestamp | YearMonthDate) {
    return new Date(b.valueOf()).getTime() - new Date(a.valueOf()).getTime()
}
